import React, { useEffect } from 'react'
import MicroModal from "micromodal"

import { EnrolledUserData, CourseUserReportData } from './data/EnrolledUsersData'
import {
  ColumnDef,
  SortingState
} from '@tanstack/react-table'

import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import Link from '../../../../common/link/Link'
import { useCourseReport } from '../context/CourseReportContext'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { getExportHeaders } from '../types/tableHeaders'
import Button from '../../../../common/button/Button'

const EnrolledUsers = ({props}) => {
  const { selectedCourse, setSelectedUser, selectedUser } = useCourseReport()
  const [rowSelection, setRowSelection] = React.useState({})
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [data, setData] = React.useState(() => CourseUserReportData( props, selectedCourse.courseId ) )
  
  useEffect(()=>{
    setData(()=> CourseUserReportData(props, selectedCourse.courseId ))
  }, [ selectedCourse, selectedUser ])
  
  MicroModal.init()

  const columns = React.useMemo<ColumnDef<EnrolledUserData>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'userId',
            accessorKey: 'userId',
            prettyName: 'User ID',
            show: false,
            cell: info => info.getValue(),
            header: () => <span>User ID</span>
          },
          {
            id: 'name',
            accessorKey: 'name',
            prettyName: 'Name',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Name</span>,
          },
          {
            id: 'email',
            accessorKey: 'email',
            prettyName: 'Email',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Email</span>,
          },
          {
            id: 'dateEnrolled',
            accessorKey: 'dateEnrolled',
            prettyName: 'Date Enrolled',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Date enrolled</span>,
          },
          { 
            id: 'dateCompleted',
            accessorKey: 'dateCompleted',
            prettyName: 'Date Completed',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Date completed</span>,
          },
          { 
            id: 'dateExpiration',
            accessorKey: 'dateExpiration',
            prettyName: 'Date Expiration',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Date expired</span>,
          },
          { 
            id: 'completePercentage',
            accessorKey: 'completePercentage',
            prettyName: 'Complete',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Complete</span>,
          },
          { 
            id: 'certificateLink',
            accessorKey: 'certificateLink',
            prettyName: 'Certificate',
            cell: (info) => (
              <Link 
                className={`msync-button msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline`} 
                link={info.getValue() === "" ? false : info.getValue() }
                target={'_blank'}
                disabled={info.getValue() === "" && true }
              >
                  Download
              </Link>
            ),
            header: () => <span>Certificate</span>,
          },
          { 
            id: 'viewEnrolledDetails',
            accessorKey: 'viewEnrolledDetail',
            prettyName: 'Action',
            cell: (info) => (
              <ViewDetails info={info.row.original} />
            ),
            header: () => <span>Details</span>,
          },
        ],
      },
    ],
    []
  )

  const exportHeaders = getExportHeaders('enrolled-users')

  const handleThisModal = (info) => {
    MicroModal.show('msync-report-user-container')
    MicroModal.close('msync-course-report-course-container')
    setSelectedUser(info)
  }

  const ViewDetails = ({info}) => {
    return (
      <Button 
        className='msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline' 
        onClick={() => handleThisModal(info)}
      >
        View
      </Button>
    )
  }

  return (
    <TanstackContent
      data={data}
      columns={columns}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      sorting={sorting}
      setSorting={setSorting}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      exportHeaders={exportHeaders}
      from={'Enrolled Users'}
      context={'Courses'}
      children={null}
      columnFilters={ null }
      setColumnFilters={ null }
    />
  )
}

export default EnrolledUsers
